<div class="consumer-insights-container">
    <div class="header-section">
        <h1 class="main-title">{{ title }}</h1>
        <p class="subtitle">Student-centric insights and enrollment analytics for higher education institutions</p>
    </div>

    <!-- Overview Score Cards -->
    @if(data?.data && data.data.length > 0){
    <div class="overview-section">
        <h2 class="section-title">
            <mat-icon>dashboard</mat-icon>
            University Performance Overview
        </h2>
        <app-score-cards [universities]="data.data"
            [metrics]="['Enrollment Rate', 'Student Satisfaction', 'Career Placement', 'Employability Prospects', 'Digital Perception']"
            [title]="'Student-Centric Performance Metrics'" [maxEntities]="5">
        </app-score-cards>
    </div>
    }

    <!-- Combined Consumer Insights Table (Single Table) -->
    @if(hasCombinedData()){
    <div class="table-container">
        <h2 class="section-title">
            <mat-icon>table_chart</mat-icon>
            Consumer Insights Summary
        </h2>
        <app-table [dataSource]="combinedTableData" [columns]="combinedTableColumns"
            [displayedColumns]="getCombinedTableColumns()">
        </app-table>
    </div>
    }

    <!-- Enrollment Analytics Section -->
    @if(showDetailedSections && hasEnrollmentData()){
    <div class="enrollment-section">
        <h2 class="section-title">
            <mat-icon>school</mat-icon>
            Enrollment Analytics
        </h2>

        <!-- Enrollment Chart -->
        @if(enrollmentChartData.length > 0){
        <div class="chart-container">
            <h3>Enrollment Rate Comparison</h3>
            <app-bar-chart [groupedBarData]="enrollmentChartData" [xAxisLabel]="'University'"
                [yAxisLabel]="'Enrollment Rate (%)'">
            </app-bar-chart>
        </div>
        }

        <!-- Enrollment Table -->
        <div class="table-container">
            <h3>Detailed Enrollment Data</h3>
            <app-table [dataSource]="enrollmentTableData" [columns]="enrollmentTableColumns"
                [displayedColumns]="getEnrollmentTableColumns()">
            </app-table>
        </div>
    </div>
    }

    <!-- Student Satisfaction Section -->
    @if(showDetailedSections && hasSatisfactionData()){
    <div class="satisfaction-section">
        <h2 class="section-title">
            <mat-icon>sentiment_satisfied</mat-icon>
            Student Satisfaction & Experience
        </h2>

        <!-- Satisfaction Radar Chart -->
        @if(satisfactionRadarData.length > 0){
        <div class="chart-container">
            <h3>Satisfaction Metrics Radar Chart</h3>
            <app-polar-chart [polarChartData]="satisfactionRadarData"></app-polar-chart>
        </div>
        }

        <!-- Satisfaction Table -->
        <div class="table-container">
            <h3>Detailed Satisfaction Metrics</h3>
            <app-table [dataSource]="satisfactionTableData" [columns]="satisfactionTableColumns"
                [displayedColumns]="getSatisfactionTableColumns()">
            </app-table>
        </div>
    </div>
    }

    <!-- Career Outcomes Section -->
    @if(showDetailedSections && hasCareerData()){
    <div class="career-section">
        <h2 class="section-title">
            <mat-icon>work</mat-icon>
            Career Outcomes & Employability
        </h2>

        <!-- Career Outcomes Chart -->
        @if(careerOutcomesChartData.length > 0){
        <div class="chart-container">
            <h3>Career Placement Rates</h3>
            <app-bar-chart [groupedBarData]="careerOutcomesChartData" [xAxisLabel]="'University'"
                [yAxisLabel]="'Placement Rate (%)'">
            </app-bar-chart>
        </div>
        }

        <!-- Career Outcomes Table -->
        <div class="table-container">
            <h3>Detailed Career Outcomes</h3>
            <app-table [dataSource]="careerTableData" [columns]="careerTableColumns"
                [displayedColumns]="getCareerTableColumns()">
            </app-table>
        </div>
    </div>
    }

    <!-- Student Demographics Section -->
    @if(showDetailedSections && hasDemographicsData()){
    <div class="demographics-section">
        <h2 class="section-title">
            <mat-icon>people</mat-icon>
            Student Demographics & Preferences
        </h2>

        <!-- Demographics Table -->
        <div class="table-container">
            <h3>Student Demographics</h3>
            <app-table [dataSource]="demographicsTableData" [columns]="demographicsTableColumns"
                [displayedColumns]="getDemographicsTableColumns()">
            </app-table>
        </div>

        <!-- Decision Factors Pie Chart -->
        @if(decisionFactorsChartData.length > 0){
        <div class="chart-container">
            <h3>Student Decision Factors</h3>
            <app-pie-grid [modelWiseScores]="decisionFactorsChartData"></app-pie-grid>
        </div>
        }
    </div>
    }

    <!-- Course Finder Integration Section -->
    <!-- @if(hasCourseFinderData()){
    <div class="course-finder-section">
        <h2 class="section-title">
            <mat-icon>search</mat-icon>
            Course Finder Integration
        </h2>

        Search Analytics
        @if(searchAnalytics){
        <div class="analytics-cards">
            <mat-card class="analytics-card">
                <mat-card-header>
                    <mat-card-title>Search Analytics</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <div class="analytics-stats">
                        <div class="stat-item">
                            <span class="stat-label">Total Searches:</span>
                            <span class="stat-value">{{ searchAnalytics.total_searches?.toLocaleString() }}</span>
                        </div>
                        <div class="stat-item">
                            <span class="stat-label">Unique Searchers:</span>
                            <span class="stat-value">{{ searchAnalytics.unique_searchers?.toLocaleString() }}</span>
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>
        </div>
        }

        Student Preferences
        @if(studentPreferences){
        <div class="preferences-section">
            <h3>Student Preferences</h3>
            <div class="preferences-grid">
                <div class="preference-card">
                    <h4>Top Concerns</h4>
                    <mat-chip-set>
                        @for(concern of studentPreferences.top_concerns; track concern){
                        <mat-chip>{{ concern }}</mat-chip>
                        }
                    </mat-chip-set>
                </div>
                <div class="preference-card">
                    <h4>Preferred Features</h4>
                    <mat-chip-set>
                        @for(feature of studentPreferences.preferred_features; track feature){
                        <mat-chip color="accent">{{ feature }}</mat-chip>
                        }
                    </mat-chip-set>
                </div>
            </div>
        </div>
        }

        Application Funnel
        @if(applicationFunnel){
        <div class="funnel-section">
            <h3>Application Funnel</h3>
            <div class="funnel-steps">
                <div class="funnel-step">
                    <div class="step-label">Awareness to Search</div>
                    <div class="step-value">{{ applicationFunnel.awareness_to_search }}%</div>
                </div>
                <div class="funnel-step">
                    <div class="step-label">Search to Application</div>
                    <div class="step-value">{{ applicationFunnel.search_to_application }}%</div>
                </div>
                <div class="funnel-step">
                    <div class="step-label">Application to Enrollment</div>
                    <div class="step-value">{{ applicationFunnel.application_to_enrollment }}%</div>
                </div>
            </div>
        </div>
        }
    </div>
    } -->

    <!-- Comparative Insights Section -->
    <!-- @if(hasComparativeInsights()){
    <div class="comparative-section">
        <h2 class="section-title">
            <mat-icon>compare</mat-icon>
            Comparative Analysis
        </h2>

        <div class="insights-grid">
            <mat-card class="insight-card">
                <mat-card-header>
                    <mat-card-title>Market Positioning</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <p>{{ comparativeInsights.market_positioning }}</p>
                </mat-card-content>
            </mat-card>

            <mat-card class="insight-card">
                <mat-card-header>
                    <mat-card-title>Competitive Advantages</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <mat-list>
                        @for(advantage of comparativeInsights.competitive_advantages; track advantage){
                        <mat-list-item>
                            <mat-icon matListItemIcon>check_circle</mat-icon>
                            <div matListItemTitle>{{ advantage }}</div>
                        </mat-list-item>
                        }
                    </mat-list>
                </mat-card-content>
            </mat-card>

            <mat-card class="insight-card">
                <mat-card-header>
                    <mat-card-title>Improvement Areas</mat-card-title>
                </mat-card-header>
                <mat-card-content>
                    <mat-list>
                        @for(area of comparativeInsights.improvement_areas; track area){
                        <mat-list-item>
                            <mat-icon matListItemIcon>warning</mat-icon>
                            <div matListItemTitle>{{ area }}</div>
                        </mat-list-item>
                        }
                    </mat-list>
                </mat-card-content>
            </mat-card>
        </div>
    </div>
    } -->

    <!-- Evidence Sources Section -->
    @if(evidenceSources.length > 0){
    <div class="evidence-section">
        <h2 class="section-title">
            <mat-icon>source</mat-icon>
            Data Sources & Evidence
        </h2>
        <div class="evidence-grid">
            @for(evidence of evidenceSources; track evidence.url){
            <mat-card class="evidence-card">
                <mat-card-header>
                    <mat-card-title>{{ evidence.source }}</mat-card-title>
                    <mat-card-subtitle>{{ evidence.category }}</mat-card-subtitle>
                </mat-card-header>
                <mat-card-content>
                    <p>{{ evidence.snippet }}</p>
                    <div class="confidence-score">
                        Confidence: {{ (evidence.confidence_score * 100).toFixed(0) }}%
                    </div>
                </mat-card-content>
                <mat-card-actions>
                    <a [href]="evidence.url" target="_blank" mat-button color="primary">
                        <mat-icon>open_in_new</mat-icon>
                        View Source
                    </a>
                </mat-card-actions>
            </mat-card>
            }
        </div>
    </div>
    }

    <!-- Recommendations Section -->
    @if(recommendations.length > 0){
    <div class="recommendations-section">
        <h2 class="section-title">
            <mat-icon>lightbulb</mat-icon>
            Strategic Recommendations
        </h2>
        <div class="recommendations-grid">
            @for(rec of recommendations; track rec.category){
            <mat-card class="recommendation-card" [class]="'priority-' + rec.priority.toLowerCase()">
                <mat-card-header>
                    <mat-card-title>{{ rec.category }}</mat-card-title>
                    <mat-card-subtitle>
                        <span class="priority-badge" [class]="'priority-' + rec.priority.toLowerCase()">
                            {{ rec.priority }}
                        </span>
                    </mat-card-subtitle>
                </mat-card-header>
                <mat-card-content>
                    <div class="recommendation-content">
                        <div class="issue-section">
                            <h4>Issue:</h4>
                            <p>{{ rec.issue }}</p>
                        </div>
                        <div class="recommendation-section">
                            <h4>Recommendation:</h4>
                            <p>{{ rec.recommendation }}</p>
                        </div>
                        <div class="impact-section">
                            <h4>Expected Impact:</h4>
                            <p>{{ rec.expected_impact }}</p>
                        </div>
                    </div>
                </mat-card-content>
            </mat-card>
            }
        </div>
    </div>
    }
</div>
